import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FacebookApiService, FacebookPageInterface, ListPagesForPartnerRequestInterface } from '@vendasta/facebook';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ConnectLeadsForPageRequestInterface, LeadsApiService, LeadSource } from '@vendasta/prospect';
import { BehaviorSubject, combineLatest, Observable, Observer, Subject } from 'rxjs';
import { finalize, map, retry, switchMap, take } from 'rxjs/operators';

@Injectable()
export class FacebookIntegrationStepperService {
  private readonly selectedPage$$ = new BehaviorSubject<string>('');
  readonly selectedPage$ = this.selectedPage$$.asObservable();
  private readonly subscribedToPage$$: Subject<boolean> = new BehaviorSubject<boolean>(false);
  readonly subscribedToPage$ = this.subscribedToPage$$.asObservable();
  private readonly isConnectingToPage$$ = new BehaviorSubject<boolean>(false);
  readonly isConnectingToPage$ = this.isConnectingToPage$$.asObservable();

  readonly pagesForPartner$: Observable<FacebookPageInterface[]> = this.partnerId$.pipe(
    map((partnerId) => <ListPagesForPartnerRequestInterface>{ partnerId: partnerId }),
    switchMap((request) => this.facebookApiService.listPagesForPartner(request)),
    retry(1),
    map((response) => response?.facebookPage),
  );

  constructor(
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private readonly leadsApiService: LeadsApiService,
    private readonly facebookApiService: FacebookApiService,
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
  ) {}

  updateSelectedPage(pageId: string): void {
    this.selectedPage$$.next(pageId);
  }

  connectSelectedPage(): void {
    this.isConnectingToPage$$.next(true);
    combineLatest([this.partnerId$, this.selectedPage$, this.pagesForPartner$])
      .pipe(
        map(([partnerId, selectedPageId, allPages]) => {
          const selectedPage = allPages.find((p) => p.id === selectedPageId);
          return [partnerId, selectedPageId, selectedPage?.name];
        }),
        switchMap(([partnerId, pageId, pageName]) =>
          this.leadsApiService.connectLeadsForPage(<ConnectLeadsForPageRequestInterface>{
            partnerId: partnerId,
            source: LeadSource.LEAD_SOURCE_FACEBOOK,
            pageId: pageId,
            pageName: pageName,
          }),
        ),
        retry(1),
        take(1),
        finalize(() => this.isConnectingToPage$$.next(false)),
      )
      .subscribe(<Observer<HttpResponse<null>>>{
        next: (resp) => {
          if (resp.status === 200) {
            this.subscribedToPage$$.next(true);
          }
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 403) {
            this.snackbarService.errorSnack(this.translate.instant('LEAD_INTEGRATIONS.FACEBOOK.SUBSCRIBE_ERROR_2FA'));
          } else {
            this.snackbarService.errorSnack(this.translate.instant('LEAD_INTEGRATIONS.FACEBOOK.SUBSCRIBE_ERROR'));
          }
        },
      });
  }
}
